import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play,
  Pause,
  Square,
  Users,
  Send,
  CheckCircle,
  TrendingUp,
  RefreshCw,
  AlertTriangle,
  Bug,
  MessageCircle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import apiService from '../../services/api';
import campaignCache from '../../utils/campaignCache';
import { MessageStatusSummary } from './MessageStatus';
import { translateCampaignStatus } from '../../utils/statusTranslations';

const CampaignProgress = ({ campaign, onCampaignEnd, onError, onSuccess }) => {
  // Obter estado do tema
  const { isDarkMode } = useTheme();
  // Estados simplificados - REMOVIDO refreshInterval
  const [currentCampaign, setCurrentCampaign] = useState(() => {
    
    
    // MELHORIA: Tentar carregar dados mais recentes do localStorage primeiro
    const cachedCampaign = campaignCache.load();
    if (cachedCampaign && campaign && cachedCampaign.id === campaign.id) {
      // Mesclar dados do cache com dados da prop
      const merged = campaignCache.merge(campaign, cachedCampaign);
      
      
      return merged;
    }
    
    
    return campaign;
  });
  const [loading, setLoading] = useState(false);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [statusStats, setStatusStats] = useState({});
  const [nextUpdateIn, setNextUpdateIn] = useState(15);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionModalShown, setCompletionModalShown] = useState(false);
  const [lastMessagesFetch, setLastMessagesFetch] = useState(0);
  const [messageFilter, setMessageFilter] = useState('all'); // 'all', 'responded', 'pending'
  

  
  // Refs para controle direto - SEM dependências circulares
  const intervalRef = useRef(null);
  const currentCampaignRef = useRef(currentCampaign);
  const debugCounterRef = useRef(0);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onCampaignEndRef = useRef(onCampaignEnd);
  
  // Sincronizar estado interno com a prop campaign quando ela mudar
  useEffect(() => {
    if (campaign && campaign !== currentCampaign) {
      
      
      
      // MELHORIA: Usar utilitário de cache para mesclar dados
      const cachedCampaign = campaignCache.load();
      if (cachedCampaign && cachedCampaign.id === campaign.id) {
        const merged = campaignCache.merge(campaign, cachedCampaign);
        
        
        setCurrentCampaign(merged);
      } else {
        // Se não há cache ou é campanha diferente, usar a prop
        
        setCurrentCampaign(campaign);
      }
    }
  }, [campaign, currentCampaign]);

  // Atualizar refs quando as props mudarem
  useEffect(() => {
    currentCampaignRef.current = currentCampaign;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onCampaignEndRef.current = onCampaignEnd;
  }, [currentCampaign, onSuccess, onError, onCampaignEnd]);

  // NOVA FUNÇÃO: Atualizar cache sempre que a campanha mudar
  useEffect(() => {
    if (currentCampaign && (currentCampaign.status === 'active' || currentCampaign.status === 'paused')) {
      campaignCache.updateIfNewer(currentCampaign);
    }
  }, [currentCampaign]);





  // Função para atualizar status da campanha
  const updateCampaignStatus = useCallback(async () => {
    const campaign = currentCampaignRef.current;
    if (!campaign?.id) {
      
      return;
    }

    
    setIsUpdating(true);
    try {
      const response = await apiService.getCampaignStatus(campaign.id);
      
      
      
      if (response.success && response.data) {
        const updatedCampaign = {
          ...campaign,
          ...response.data,
          sentCount: response.data.sent_count || response.data.sentCount || campaign.sentCount || 0,
          status: response.data.status || campaign.status,
          totalContacts: response.data.total_contacts || response.data.totalContacts || campaign.totalContacts || 0,
          // Preservar propriedades importantes que podem não vir da API
          message: campaign.message, // Preservar mensagem da campanha
          interval: campaign.interval // Preservar intervalo da campanha
        };
        
        
        
        
        
        
        
        
        
        
        // Verificar se houve mudança real nos dados
        const hasChanges = 
          updatedCampaign.sentCount !== campaign.sentCount ||
          updatedCampaign.status !== campaign.status ||
          updatedCampaign.totalContacts !== campaign.totalContacts;
          
        if (hasChanges) {
          
        }
        

        
        setCurrentCampaign(updatedCampaign);

        // Atualizar cache se a campanha ainda estiver ativa ou pausada
        if (response.data.status === 'active' || response.data.status === 'paused') {
          campaignCache.save(updatedCampaign);
        } else {
          // Remover do cache se a campanha foi concluída ou parada
          campaignCache.clear();
        }

        // Se a campanha foi concluída ou parada, parar o refresh
        if (response.data.status === 'completed' || response.data.status === 'stopped') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          
          // Fazer uma última busca das mensagens para garantir dados atualizados
          try {
            const finalMessagesResponse = await apiService.getCampaignMessages(campaign.id, 1, 100);
            if (finalMessagesResponse.success && finalMessagesResponse.data) {
              setCampaignMessages(finalMessagesResponse.data.messages || []);
              setStatusStats(finalMessagesResponse.data.statusStats || {});
            }
          } catch (finalError) {
          }
          
          // Mostrar mensagem de conclusão
          if (response.data.status === 'completed') {
            // Notificação removida
          }
        }
      }

      // Buscar mensagens separadamente - usando controle de tempo
      const now = Date.now();
      if (now - lastMessagesFetch > 2000) {
        setLastMessagesFetch(now);
        setMessagesLoading(true);
        try {
          const messagesResponse = await apiService.getCampaignMessages(campaign.id, 1, 100);
          if (messagesResponse.success && messagesResponse.data) {
            setCampaignMessages(messagesResponse.data.messages || []);
            setStatusStats(messagesResponse.data.statusStats || {});
          }
        } catch (msgError) {
        } finally {
          setMessagesLoading(false);
        }
      }
    } catch (error) {
    } finally {
      setIsUpdating(false);
    }
  }, [lastMessagesFetch]); // Incluindo lastMessagesFetch como dependência



  // Função para calcular progresso
  const calculateProgress = useCallback(() => {
    if (!currentCampaign) {
      
      return 0;
    }
    
    const total = currentCampaign.totalContacts || 0;
    const sentFromCampaign = currentCampaign.sentCount || 0;
    const sentFromStats = statusStats.sent || 0;
    
    // TESTE: Usar o maior valor entre os dois
    const sent = Math.max(sentFromCampaign, sentFromStats);
    
    
    
    
    
    
    
    if (total === 0) return 0;
    
    return Math.round((sent / total) * 100);
  }, [currentCampaign, statusStats]);

  // Função para calcular número de mensagens enviadas (usando a mesma lógica do progresso)
  const calculateSentCount = useCallback(() => {
    if (!currentCampaign) return 0;
    
    const sentFromCampaign = currentCampaign.sentCount || 0;
    const sentFromStats = statusStats.sent || 0;
    
    // Usar o maior valor entre os dois (mesma lógica do calculateProgress)
    return Math.max(sentFromCampaign, sentFromStats);
  }, [currentCampaign, statusStats]);

  // Função para pausar campanha
  const pauseCampaign = useCallback(async () => {
    const campaign = currentCampaignRef.current;
    if (!campaign?.id) return;

    
    
    
    setLoading(true);
    try {
      const response = await apiService.toggleCampaign(campaign.id, 'pause');
      
      
      
      if (response.success) {
        const updatedCampaign = { ...campaign, status: 'paused' };
        
        
        setCurrentCampaign(updatedCampaign);
        
        // Atualizar cache
        campaignCache.save(updatedCampaign);
        // Notificação removida
        
        // Parar o polling quando pausar
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          
        }
        
        // Aguardar um pouco e então recarregar a página para garantir que as mudanças sejam refletidas
        setTimeout(() => {
          
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(response.message || 'Falha ao pausar campanha');
      }
    } catch (error) {
      
      onErrorRef.current('Erro ao pausar campanha: ' + error.message);
      // Resetar loading apenas em caso de erro (não redireciona)
      setLoading(false);
    }
    // Loading permanece ativo até o redirecionamento da página
  }, []); // SEM DEPENDÊNCIAS para evitar loop

  // Detectar quando a campanha termina de enviar todas as mensagens
  useEffect(() => {
    const isActive = currentCampaign?.status === 'active';
    const totalContacts = currentCampaign?.totalContacts || 0;
    
    if (isActive && totalContacts > 0 && !showCompletionModal && !completionModalShown) {
      const progress = calculateProgress();
      const sentCount = calculateSentCount();
      
      // Se a campanha está ativa, terminou de enviar todas as mensagens e o modal ainda não foi mostrado
      if (progress >= 100 && sentCount >= totalContacts) {
        setShowCompletionModal(true);
        setCompletionModalShown(true);
      }
    }
  }, [currentCampaign?.sentCount, statusStats.sent, currentCampaign?.totalContacts, currentCampaign?.status, showCompletionModal, completionModalShown, calculateProgress, calculateSentCount]);

  // Função para retomar campanha
  const resumeCampaign = useCallback(async () => {
    const campaign = currentCampaignRef.current;
    if (!campaign?.id) return;


    
    setLoading(true);
    try {
      const response = await apiService.toggleCampaign(campaign.id, 'resume');
      
      if (response.success) {
        const updatedCampaign = { ...campaign, status: 'active' };
        setCurrentCampaign(updatedCampaign);
        // Atualizar cache
        campaignCache.save(updatedCampaign);
        // Notificação removida
        
        // O refresh será reiniciado automaticamente pelo useEffect quando o status mudar para 'active'
        
        // Aguardar um pouco e então recarregar a página para garantir que as mudanças sejam refletidas
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(response.message || 'Falha ao retomar campanha');
      }
    } catch (error) {
      onErrorRef.current('Erro ao retomar campanha: ' + error.message);
      // Resetar loading apenas em caso de erro (não redireciona)
      setLoading(false);
    }
    // Loading permanece ativo até o redirecionamento da página
  }, []); // SEM DEPENDÊNCIAS para evitar loop

  // Função para abrir modal de confirmação
  const openStopModal = useCallback(() => {
    setShowStopModal(true);
  }, []);

  // Função para parar campanha (executada após confirmação no modal)
  const stopCampaign = useCallback(async () => {
    const campaign = currentCampaignRef.current;
    if (!campaign?.id) {
      onErrorRef.current('Erro: Campanha inválida');
      return;
    }

    // NÃO fechar o modal imediatamente - manter aberto com loading
    // setShowStopModal(false); // Comentado para manter modal aberto

    // FEEDBACK VISUAL IMEDIATO
    setCurrentCampaign(prev => ({ ...prev, status: 'stopping' })); // Status temporário
    
    // Parar polling imediatamente para evitar conflitos
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setLoading(true);
    
    try {
      const response = await apiService.stopCampaign(campaign.id);
      
      if (response.success) {
        setCurrentCampaign(prev => ({ ...prev, status: 'stopped' }));
        campaignCache.clear();
        // Notificação removida
        
        // Modal será fechado apenas quando a página for redirecionada
        // setShowStopModal(false); // Comentado para manter modal aberto
        
        // Aguardar um pouco antes de redirecionar (loading permanece ativo)
        setTimeout(() => {
          onCampaignEndRef.current();
        }, 2000);
      } else {
        throw new Error(response.message || 'Falha ao parar campanha no servidor');
      }
    } catch (error) {
      
      // Verificar se é erro de rede ou servidor
      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR';
      
      if (isNetworkError) {
        setCurrentCampaign(prev => ({ ...prev, status: 'stopped' }));
        campaignCache.clear();
        onErrorRef.current('Erro de conexão, mas campanha foi parada localmente');
        
        // Modal será fechado apenas quando a página for redirecionada
        // setShowStopModal(false); // Comentado para manter modal aberto
      } else {
        setCurrentCampaign(prev => ({ ...prev, status: campaign.status })); // Reverter status
        onErrorRef.current(`Erro ao parar campanha: ${error.message}`);
        
        // Modal será fechado apenas quando a página for redirecionada
        // setShowStopModal(false); // Comentado para manter modal aberto
        
        // Resetar loading apenas em caso de erro do servidor (não redireciona)
        setLoading(false);
        return; // Não redirecionar em caso de erro do servidor
      }
      
      // Só redirecionar se conseguiu parar localmente
      setTimeout(() => {
        onCampaignEndRef.current();
      }, 2000);
    }
    // Não resetar loading no finally - será resetado apenas quando a página redirecionar
    // finally {
    //   setLoading(false);
    // }
  }, []); // SEM DEPENDÊNCIAS para evitar loop

  // Função para finalizar campanha (quando terminou de enviar todas as mensagens)
  const finalizeCampaign = useCallback(async () => {
    const campaign = currentCampaignRef.current;
    if (!campaign?.id) {
      onErrorRef.current('Erro: Campanha inválida');
      return;
    }

    // FEEDBACK VISUAL IMEDIATO
    setCurrentCampaign(prev => ({ ...prev, status: 'completing' })); // Status temporário
    
    // Parar polling imediatamente
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setLoading(true);
    
    try {
      const response = await apiService.stopCampaign(campaign.id);
      
      if (response.success) {
        setCurrentCampaign(prev => ({ ...prev, status: 'completed' }));
        campaignCache.clear();
        // Notificação removida
        
        // Aguardar um pouco antes de redirecionar
        setTimeout(() => {
          onCampaignEndRef.current();
        }, 2000);
      } else {
        throw new Error(response.message || 'Falha ao finalizar campanha no servidor');
      }
    } catch (error) {
      
      
      // Verificar se é erro de rede ou servidor
      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR';
      
      if (isNetworkError) {
        
        setCurrentCampaign(prev => ({ ...prev, status: 'completed' }));
        campaignCache.clear();
        onErrorRef.current('Erro de conexão, mas campanha foi finalizada localmente');
        
        // Aguardar um pouco antes de redirecionar
        setTimeout(() => {
          onCampaignEndRef.current();
        }, 2000);
      } else {
        
        setCurrentCampaign(prev => ({ ...prev, status: campaign.status })); // Reverter status
        onErrorRef.current(`Erro ao finalizar campanha: ${error.message}`);
        
        // Resetar loading apenas em caso de erro do servidor (não redireciona)
        setLoading(false);
      }
    }
    // Não resetar loading no finally - será resetado apenas quando a página redirecionar
    // finally {
    //   setLoading(false);
    // }
  }, []); // SEM DEPENDÊNCIAS para evitar loop



  // useEffect principal - controlar polling baseado no status
  useEffect(() => {
    if (currentCampaign?.status === 'active') {
      
      // Limpar intervalo anterior se existir
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Criar novo intervalo
      debugCounterRef.current++;
      const intervalId = setInterval(() => {
        updateCampaignStatus();
        setNextUpdateIn(15); // Reset countdown para 15 segundos
      }, 15000); // Intervalo de 15 segundos
      
      intervalRef.current = intervalId;
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    
    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentCampaign?.status, updateCampaignStatus]);

  // Cleanup global quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Sincronizar com mudanças na prop campaign - APENAS se não estivermos em uma operação de pause/resume
  useEffect(() => {
    if (campaign && campaign.id !== currentCampaign?.id) {
      setCurrentCampaign(campaign);
    }
  }, [campaign, currentCampaign?.id]);

  // Atualizar status inicial - APENAS UMA VEZ ao montar
  useEffect(() => {
    if (campaign?.id) {
      updateCampaignStatus();
    }
  }, [campaign?.id, updateCampaignStatus]);

  // Countdown timer para próxima atualização
  useEffect(() => {
    if (!currentCampaign || currentCampaign.status !== 'active') return;

    const countdownInterval = setInterval(() => {
      setNextUpdateIn(prev => {
        if (prev <= 1) {
          return 15; // Reset para 15 segundos
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [currentCampaign]);

  // Early return se não há campanha
  if (!currentCampaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Nenhuma campanha ativa encontrada.</p>
      </div>
    );
  }

  const progress = calculateProgress();
  const isActive = currentCampaign.status === 'active';
  const isPaused = currentCampaign.status === 'paused';
  const isCompleted = currentCampaign.status === 'completed' || currentCampaign.status === 'stopped';
  
  // Verificar se a campanha terminou de enviar todas as mensagens mas ainda está ativa
  const isFinishedButActive = isActive && progress >= 100 && calculateSentCount() >= (currentCampaign.totalContacts || 0);
  


  // Funções de debug
  const testResponseDetection = async () => {
    try {
      const recentMessages = await apiService.getRecentMessages(5);
      
      if (recentMessages.success && recentMessages.messages.length > 0) {
        // Notificação removida
      } else {
        onError('Nenhuma mensagem encontrada no banco de dados.');
      }
    } catch (error) {
      onError('Erro ao testar detecção de respostas: ' + error.message);
    }
  };



  return (
    <div className="space-y-6">
      {/* Header da Campanha */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {isPaused ? 'Campanha Pausada' : 'Campanha em Andamento'}
            </h2>
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                isActive 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : isPaused
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
              }`}>
                {translateCampaignStatus(currentCampaign.status)}
              </span>
              <span>Intervalo: {currentCampaign.interval || 5} minutos</span>
            </div>
          </div>
          
          {/* Controles da Campanha */}
          <div className="flex space-x-2">
            {isActive && (
              <button
                onClick={pauseCampaign}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Pause className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Pausando...' : 'Pausar'}
              </button>
            )}
            
            {isPaused && (
              <button
                onClick={resumeCampaign}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Retomando...' : 'Continuar Campanha'}
              </button>
            )}
            
            {!isCompleted && !isFinishedButActive && (
              <button
                onClick={openStopModal}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                <Square className="w-4 h-4 mr-2" />
                Parar
              </button>
            )}
            
            {isFinishedButActive && (
              <button
                onClick={finalizeCampaign}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Finalizar
              </button>
            )}
            
            {isCompleted && (
              <button
                onClick={onCampaignEnd}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Criar Nova Campanha
              </button>
            )}
          </div>
        </div>

        {/* Barra de Progresso */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progresso da Campanha
            </span>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {progress}% concluído
              </span>
              {isActive && (
                <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                  Ativo
                </div>
              )}
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                isCompleted 
                  ? 'bg-green-600' 
                  : isActive 
                  ? 'bg-blue-600 animate-pulse' 
                  : 'bg-yellow-600'
              }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          {/* Indicador de progresso em tempo real */}
          {isActive && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
              <span>
                {calculateSentCount()} de {currentCampaign.totalContacts || 0} enviadas
              </span>
              <span className="flex items-center">
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                Próxima atualização em {nextUpdateIn}s
              </span>
            </div>
          )}
          
          {/* Indicador de progresso para campanhas finalizadas */}
          {(isCompleted || currentCampaign?.status === 'stopped') && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {calculateSentCount()} de {currentCampaign.totalContacts || 0} enviadas
                {isCompleted ? ' - Campanha concluída' : ' - Campanha parada'}
              </span>
            </div>
          )}
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Users className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentCampaign.totalContacts || 0}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Send className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {statusStats.sent || 0}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Enviadas</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {statusStats.responded || 0}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Respondidas</p>
          </div>
        </div>

        {/* Alerta para Campanha que Terminou de Enviar */}
        {isFinishedButActive && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Campanha terminou de enviar todas as mensagens
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Todas as {currentCampaign.totalContacts || 0} mensagens foram enviadas com sucesso. 
                  Você pode finalizar a campanha agora ou aguardar mais respostas antes de finalizar.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Alerta de Debug para Respostas */}
        {statusStats.responded === 0 && statusStats.sent > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Nenhuma resposta detectada
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Se você recebeu respostas no WhatsApp mas elas não aparecem aqui, pode haver um problema na configuração do webhook.
                </p>
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => testResponseDetection()}
                    className="flex items-center px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <Bug className="w-4 h-4 mr-1" />
                    Testar Detecção
                  </button>

                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Detalhes da Mensagem */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Mensagem da Campanha
        </h3>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {currentCampaign.message || 'Mensagem não disponível'}
          </p>
        </div>
      </div>

      {/* Lista de Mensagens com Status Detalhado */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Status das Mensagens
            </h3>
            <button
              onClick={updateCampaignStatus}
              disabled={messagesLoading || isUpdating}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${messagesLoading || isUpdating ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
          
          {/* Filtros de Mensagens */}
          <div className="flex space-x-2">
            <button
              onClick={() => setMessageFilter('all')}
              className={`px-3 py-1 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 ${
                messageFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md'
              }`}
            >
              Todas ({campaignMessages.length})
            </button>
            <button
              onClick={() => setMessageFilter('responded')}
              className={`px-3 py-1 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 ${
                messageFilter === 'responded'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md'
              }`}
            >
              Respondidas ({campaignMessages.filter(msg => msg.response_text).length})
            </button>
            <button
              onClick={() => setMessageFilter('pending')}
              className={`px-3 py-1 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 ${
                messageFilter === 'pending'
                  ? 'bg-yellow-600 text-white shadow-lg shadow-yellow-500/25'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md'
              }`}
            >
              Sem Resposta ({campaignMessages.filter(msg => !msg.response_text).length})
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {messagesLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-500 dark:text-gray-400">Carregando mensagens...</p>
            </div>
          ) : (() => {
            // Aplicar filtros
            const filteredMessages = campaignMessages.filter(message => {
              if (messageFilter === 'responded') return message.response_text;
              if (messageFilter === 'pending') return !message.response_text;
              return true; // 'all'
            });

            return filteredMessages.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredMessages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`p-4 border rounded-lg transition-all duration-300 hover:shadow-md transform hover:scale-[1.02] animate-slide-in-from-top ${
                      message.response_text
                        ? 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {message.contact_name || 'Nome não informado'}
                          </p>
                          {message.response_text && (
                            <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full font-medium">
                              Respondeu
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          📱 {message.phone || 'Telefone não informado'}
                        </p>
                        
                        {message.response_text && (
                          <div className="mt-3 p-3 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0 mt-1">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1">
                                  Resposta recebida:
                                </p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  "{message.response_text}"
                                </p>
                                {message.response_at && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {new Date(message.response_at).toLocaleString('pt-BR')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-3 ml-4">
                        <MessageStatusSummary
                          status={message.delivery_status || 'sent'}
                          timestamp={message.sent_at}
                          responseText={message.response_text}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {messageFilter === 'responded' 
                    ? 'Nenhuma mensagem respondida encontrada'
                    : messageFilter === 'pending'
                    ? 'Nenhuma mensagem sem resposta encontrada'
                    : 'Nenhuma mensagem encontrada para esta campanha'
                  }
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Modal de Confirmação para Parar Campanha */}
      {showStopModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 animate-slideInUp hover:shadow-2xl">
            <div className="p-6">
              {/* Header do Modal */}
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <Square className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Parar Campanha
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Confirme a ação abaixo
                  </p>
                </div>
              </div>

              {/* Conteúdo do Modal */}
              <div className="mb-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                        Atenção: Ação Irreversível
                      </h4>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        Esta ação não pode ser desfeita. A campanha será interrompida permanentemente.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Tem certeza que deseja parar esta campanha?
                </p>

                {/* Informações da Campanha */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Status atual:</span>
                      <span className={`font-medium ${
                        currentCampaign?.status === 'active' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {translateCampaignStatus(currentCampaign?.status)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Progresso:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {calculateProgress()}% concluído
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Mensagens enviadas:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {currentCampaign?.sentCount || 0} de {currentCampaign?.totalContacts || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Ao parar a campanha, todas as mensagens pendentes serão canceladas e não será possível retomar o envio.
                </p>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3">
                <button
                  onClick={() => !loading && setShowStopModal(false)}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                    loading 
                      ? 'cursor-not-allowed opacity-50' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  } border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}
                >
                  {loading ? 'Processando...' : 'Cancelar'}
                </button>
                <button
                  onClick={stopCampaign}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Parando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Square className="w-4 h-4 mr-2" />
                      Parar Campanha
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Conclusão da Campanha */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 animate-slideInUp hover:shadow-2xl">
            <div className="p-6">
              {/* Header do Modal */}
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Campanha Concluída!
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Todas as mensagens foram enviadas
                  </p>
                </div>
              </div>

              {/* Conteúdo do Modal */}
              <div className="mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                        Envio Completo
                      </h4>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        Sua campanha terminou de enviar todas as {currentCampaign?.totalContacts || 0} mensagens com sucesso!
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  O que você gostaria de fazer agora?
                </p>

                {/* Informações da Campanha */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Total de mensagens:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {currentCampaign?.totalContacts || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Mensagens enviadas:</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {calculateSentCount()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Respostas recebidas:</span>
                      <span className="font-medium text-purple-600 dark:text-purple-400">
                        {statusStats.responded || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Você pode finalizar a campanha agora ou ver os detalhes para acompanhar as respostas antes de finalizar.
                </p>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    // Permitir acesso à página de detalhes
                    // O modal será fechado e o usuário poderá ver a página completa
                  }}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                    loading 
                      ? 'cursor-not-allowed opacity-50' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  } border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300`}
                >
                  <span className="flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Ver Detalhes
                  </span>
                </button>
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    finalizeCampaign();
                  }}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Finalizando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finalizar
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default CampaignProgress;